import {
    api,
    apiErrorMessage,
    useAuthStore,
    useIsPlatformHost,
} from '@bemor/web-kit';
import {
    Badge,
    Button,
    ConfirmDialog,
    Field,
    FormSheet,
    Input,
    PageHeader,
    RowActions,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Switch,
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
    Textarea,
    type ListColumnDef,
} from '@bemor/web-kit/ui';
import { useMemo, useState } from 'react';

import type {
    RiskList,
    RiskProfile,
    RiskProfileDiff,
    RiskProfileStats,
    RiskRule,
    RiskRuleField,
    RiskRuleStats,
    RiskRuleValidation,
} from '@/api/types';
import {
    AdminListPage,
    adminListPrefetch,
    filterDefs,
    useAdminList,
} from '@/lib/lists';
import { P } from '@/lib/permissions';
import { useOne, useWrite } from '@/lib/queries';
import {
    ConditionEditor,
    type ConditionNode,
} from '@/pages/risk/condition-editor';
import {
    RuleLabelsChart,
    RuleStatsChart,
    totalsSummary,
} from '@/pages/risk/learning';

const PATH = '/staff/risk-rules';
const PAGE_KEY = 'risk-rules';
const PROFILES = '/staff/risk/profiles';

export const riskRulesPrefetch = adminListPrefetch(PAGE_KEY, PATH);

const ruleFilters = filterDefs(PATH, {
    q: 'Search',
    status: 'Status',
    stage: 'Stage',
    origin: 'Origin',
    type: 'Type',
});

export const STAGES = ['pre_auth', 'post_auth', 'both'] as const;
export const STATUSES = ['draft', 'shadow', 'active', 'retired'] as const;
export const OVERRIDES = [
    '',
    'allow',
    'require_3ds',
    'review',
    'decline',
] as const;

const statusTone = (s: string) =>
    s === 'active' ? 'success' : s === 'shadow' ? 'warning' : 'neutral';

/** The resolved row of the list: the set row plus where it comes from (origin, overridden). */
type Row = RiskRule & {
    origin?: string;
    overridden?: boolean;
    condition_text?: string;
    /** slice 5: the 30 day hits from the profile stats, joined by key on the page */
    hits_30d?: number;
};

const columns: ListColumnDef<Row>[] = [
    {
        key: 'key',
        label: 'Key',
        cell: (r) => <span className="font-mono text-xs">{r.key}</span>,
        sortKey: 'key',
        locked: 'start',
        noTruncate: true,
    },
    { key: 'name', label: 'Name', cell: (r) => r.name, sortKey: 'name' },
    {
        key: 'origin',
        label: 'Origin',
        cell: (r) => (
            <span className="flex items-center gap-1">
                {r.origin ?? r.source}
                {r.overridden ? (
                    <Badge variant="outline">overridden</Badge>
                ) : null}
            </span>
        ),
        sortKey: 'origin',
    },
    {
        key: 'stage',
        label: 'Stage',
        cell: (r) => r.stage ?? '—',
        sortKey: 'stage',
    },
    {
        key: 'condition_text',
        label: 'Condition',
        cell: (r) => r.condition_text ?? '',
    },
    {
        key: 'action_override',
        label: 'Action',
        cell: (r) => r.action_override ?? 'score',
    },
    {
        key: 'score',
        label: 'Score',
        cell: (r) => <span className="tabular-nums">{r.score}</span>,
        sortKey: 'score',
        noTruncate: true,
    },
    {
        key: 'status',
        label: 'Status',
        cell: (r) => (
            <span className="flex items-center gap-1">
                <Badge variant={statusTone(r.status)}>{r.status}</Badge>
                {r.breaker_tripped_at ? (
                    <Badge variant="destructive" data-testid="rule-tripped">
                        Tripped
                    </Badge>
                ) : null}
            </span>
        ),
        sortKey: 'status',
        noTruncate: true,
    },
    // slice 5 (decision 12): the last 30 days' live hits, from the profile stats
    {
        key: 'hits_30d',
        label: 'Hits 30d',
        cell: (r) => <span className="tabular-nums">{r.hits_30d ?? '—'}</span>,
        noTruncate: true,
    },
];

/** BM-125 slice 5 (decisions 12, 44): one rule's performance and breaker state, read-only. */
function RuleStatsDialog({
    rule,
    onClose,
}: {
    rule: Row;
    onClose: () => void;
}) {
    const stats = useOne<RiskRuleStats>(`${PATH}/${rule.id}/stats`);
    const s = stats.data?.data;
    return (
        <ConfirmDialog
            open
            onOpenChange={(o) => (o ? null : onClose())}
            title={`${rule.key} performance`}
            confirmLabel="Close"
            onConfirm={onClose}
            // BM-158 QA: the figures are block content, so they go in children (the body is the dialog's <p>
            // description, and blocks inside it were a hydration error)
            body="Hits, declines and the later labels of the last 90 days."
        >
            {s ? (
                <div className="space-y-3 text-sm" data-testid="rule-stats">
                    <p className="m-0">
                        Last 30 days: {totalsSummary(s.totals_30d)}
                    </p>
                    <p className="text-fg-2 m-0">
                        Last 90 days: {totalsSummary(s.totals_90d)}
                    </p>
                    {s.breaker.configured ? (
                        <p className="m-0">
                            Breaker: shadow when declines pass{' '}
                            {s.breaker.max_decline_share_24h_bps == null
                                ? '—'
                                : `${s.breaker.max_decline_share_24h_bps / 100}%`}{' '}
                            of attempts in 24 h or the wrong-decline estimate
                            passes{' '}
                            {s.breaker.max_false_positive_bps == null
                                ? '—'
                                : `${s.breaker.max_false_positive_bps / 100}%`}
                            .
                            {s.breaker.tripped ? (
                                <span className="text-destructive">
                                    {' '}
                                    Tripped: {s.breaker.reason}. Staff set the
                                    rule active again from its status menu.
                                </span>
                            ) : null}
                        </p>
                    ) : (
                        <p className="text-fg-2 m-0">
                            No breaker on this rule.
                        </p>
                    )}
                    <RuleStatsChart rows={s.days} />
                    <RuleLabelsChart rows={s.days} />
                </div>
            ) : (
                <p className="text-fg-2 m-0 text-sm">Loading…</p>
            )}
        </ConfirmDialog>
    );
}

/** A parameter row of the rule: a name and a JSON-ish value (number, true/false, text). */
const paramValue = (text: string): unknown =>
    text === 'true'
        ? true
        : text === 'false'
          ? false
          : /^-?\d+(\.\d+)?$/.test(text.trim())
            ? Number(text)
            : text;

export function RuleSheet({
    rule,
    profile,
    fields,
    lists,
    ruleKeys,
    onClose,
}: {
    /** null: a new rule of the board's own profile */
    rule: Row | null;
    profile: RiskProfile | null;
    fields: readonly RiskRuleField[];
    lists: readonly RiskList[];
    ruleKeys: readonly string[];
    onClose: () => void;
}) {
    const [key, setKey] = useState(rule?.key ?? '');
    const [name, setName] = useState(rule?.name ?? '');
    const [description, setDescription] = useState(rule?.description ?? '');
    const [stage, setStage] = useState<string>(rule?.stage ?? 'pre_auth');
    const [status, setStatus] = useState<string>(rule?.status ?? 'draft');
    const [score, setScore] = useState(String(rule?.score ?? 10));
    const [override, setOverride] = useState<string>(
        rule?.action_override ?? '',
    );
    const [failClosed, setFailClosed] = useState(Boolean(rule?.fail_closed));
    const [explanation, setExplanation] = useState(
        rule?.explanation_template ?? '',
    );
    const [params, setParams] = useState<{ k: string; v: string }[]>(
        Object.entries((rule?.params as Record<string, unknown>) ?? {}).map(
            ([k, v]) => ({ k, v: String(v) }),
        ),
    );
    const [condition, setCondition] = useState<ConditionNode>(
        (rule?.condition as ConditionNode) ?? { all: [] },
    );
    const [json, setJson] = useState(() => JSON.stringify(condition, null, 2));
    const [jsonError, setJsonError] = useState<string | null>(null);
    const [preview, setPreview] = useState<RiskRuleValidation | null>(null);
    const [error, setError] = useState<unknown>(null);
    const body = () => ({
        rule_key: key.trim().toUpperCase(),
        name: name.trim(),
        description,
        stage,
        status,
        score: Number.parseInt(score, 10) || 0,
        action_override: override || null,
        fail_closed: failClosed,
        explanation_template: explanation,
        params: Object.fromEntries(
            params
                .filter((p) => p.k.trim())
                .map((p) => [p.k.trim(), paramValue(p.v)]),
        ),
        condition,
        ...(profile && !rule ? { profile_id: profile.id } : {}),
    });
    const write = useWrite(async () => {
        if (rule)
            return (await api().put<RiskRule>(`${PATH}/${rule.id}`, body()))
                .data;
        return (await api().post<RiskRule>(PATH, body())).data;
    }, [PATH, PROFILES]);
    const describe = useWrite(
        async () =>
            (await api().post<RiskRuleValidation>(`${PATH}/validate`, body()))
                .data,
        [],
    );
    const syncJson = (next: ConditionNode) => {
        setCondition(next);
        setJson(JSON.stringify(next, null, 2));
    };
    const paramNames = params.map((p) => p.k.trim()).filter(Boolean);
    return (
        <FormSheet
            open
            onOpenChange={(o) => (o ? null : onClose())}
            size="lg"
            title={rule ? `Edit ${rule.key}` : 'New rule'}
            submitLabel={rule ? 'Save changes' : 'Create rule'}
            busy={write.isPending}
            submitDisabled={name.trim() === '' || jsonError !== null}
            dirty
            error={error}
            data-testid="risk-rule-form"
            onSubmit={(e) => {
                e.preventDefault();
                setError(null);
                write.mutate(undefined, {
                    onSuccess: onClose,
                    onError: setError,
                });
            }}
        >
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <Field
                    id="rule-key"
                    label="Key"
                    hint="Letters, digits, dashes; empty: numbered for you"
                >
                    <Input
                        id="rule-key"
                        value={key}
                        disabled={rule !== null}
                        onChange={(e) => setKey(e.target.value)}
                    />
                </Field>
                <Field id="rule-name" label="Name">
                    <Input
                        id="rule-name"
                        maxLength={120}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                    />
                </Field>
            </div>
            <Field id="rule-description" label="Description">
                <Textarea
                    id="rule-description"
                    rows={2}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                />
            </Field>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                <Field id="rule-stage" label="Stage">
                    <Select value={stage} onValueChange={setStage}>
                        <SelectTrigger id="rule-stage">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {STAGES.map((s) => (
                                <SelectItem key={s} value={s}>
                                    {s}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </Field>
                <Field
                    id="rule-status"
                    label="Status"
                    hint="Shadow records hits, acts on none"
                >
                    <Select value={status} onValueChange={setStatus}>
                        <SelectTrigger id="rule-status">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {STATUSES.map((s) => (
                                <SelectItem key={s} value={s}>
                                    {s}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </Field>
                <Field
                    id="rule-score"
                    label="Score"
                    hint="0 to 100, added to the check"
                >
                    <Input
                        id="rule-score"
                        inputMode="numeric"
                        value={score}
                        onChange={(e) => setScore(e.target.value)}
                    />
                </Field>
                <Field
                    id="rule-override"
                    label="Action"
                    hint="Empty: the rule only scores"
                >
                    <Select
                        value={override || 'score'}
                        onValueChange={(v) =>
                            setOverride(v === 'score' ? '' : v)
                        }
                    >
                        <SelectTrigger id="rule-override">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {OVERRIDES.map((o) => (
                                <SelectItem
                                    key={o || 'score'}
                                    value={o || 'score'}
                                >
                                    {o || 'score only'}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </Field>
            </div>
            <Tabs defaultValue="builder">
                <TabsList>
                    <TabsTrigger value="builder">Condition</TabsTrigger>
                    <TabsTrigger value="json">Advanced (JSON)</TabsTrigger>
                </TabsList>
                <TabsContent value="builder" className="pt-2">
                    <ConditionEditor
                        node={condition}
                        onChange={syncJson}
                        fields={fields}
                        params={paramNames}
                        lists={lists.map((l) => l.name)}
                        ruleKeys={ruleKeys.filter(
                            (k) => k !== key.trim().toUpperCase(),
                        )}
                        stage={stage}
                    />
                </TabsContent>
                <TabsContent value="json" className="pt-2">
                    <Textarea
                        aria-label="Condition JSON"
                        className="font-mono text-xs"
                        rows={12}
                        value={json}
                        onChange={(e) => {
                            setJson(e.target.value);
                            try {
                                const parsed = JSON.parse(
                                    e.target.value,
                                ) as ConditionNode;
                                setCondition(parsed);
                                setJsonError(null);
                            } catch (err) {
                                setJsonError((err as Error).message);
                            }
                        }}
                    />
                    {jsonError ? (
                        <p className="text-destructive mt-1 text-xs">
                            {jsonError}
                        </p>
                    ) : null}
                </TabsContent>
            </Tabs>
            <fieldset className="space-y-2">
                <legend className="text-sm font-medium">Parameters</legend>
                <p className="text-fg-2 text-xs">
                    Named numbers the condition reads with "param"; a tenant
                    overrides them without editing the rule.
                </p>
                {params.map((p, i) => (
                    <div key={i} className="flex flex-wrap gap-2">
                        <Input
                            aria-label="Parameter name"
                            className="w-48"
                            placeholder="max_cards"
                            value={p.k}
                            onChange={(e) =>
                                setParams(
                                    params.map((q, j) =>
                                        j === i
                                            ? { ...q, k: e.target.value }
                                            : q,
                                    ),
                                )
                            }
                        />
                        <Input
                            aria-label="Parameter value"
                            className="w-40"
                            placeholder="5"
                            value={p.v}
                            onChange={(e) =>
                                setParams(
                                    params.map((q, j) =>
                                        j === i
                                            ? { ...q, v: e.target.value }
                                            : q,
                                    ),
                                )
                            }
                        />
                        <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            aria-label="Remove parameter"
                            onClick={() =>
                                setParams(params.filter((_, j) => j !== i))
                            }
                        >
                            ×
                        </Button>
                    </div>
                ))}
                <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setParams([...params, { k: '', v: '' }])}
                >
                    Add parameter
                </Button>
            </fieldset>
            <Field
                id="rule-explanation"
                label="Explanation"
                hint="Shown on the check: {{value}}, {{label}}, |pct, |usd, |x"
            >
                <Input
                    id="rule-explanation"
                    value={explanation}
                    onChange={(e) => setExplanation(e.target.value)}
                />
            </Field>
            <div className="flex items-center justify-between gap-3">
                <div>
                    <p className="text-sm font-medium">Fail closed</p>
                    <p className="text-fg-2 text-xs">
                        A source this rule needs that is unavailable stops the
                        sale (503) instead of reading as unknown.
                    </p>
                </div>
                <Switch
                    aria-label="Fail closed"
                    checked={failClosed}
                    onCheckedChange={setFailClosed}
                />
            </div>
            <div className="space-y-2" data-testid="rule-describe">
                <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={describe.isPending}
                    onClick={() =>
                        describe.mutate(undefined, {
                            onSuccess: setPreview,
                            onError: (e) =>
                                setPreview({
                                    valid: false,
                                    errors: [
                                        {
                                            path: '',
                                            message:
                                                apiErrorMessage(e) ??
                                                'Could not validate',
                                        },
                                    ],
                                    condition_text: '',
                                }),
                        })
                    }
                >
                    Describe and validate
                </Button>
                {preview ? (
                    <div className="text-sm">
                        <p className="m-0">{preview.condition_text}</p>
                        {preview.errors.map((e, i) => (
                            <p key={i} className="text-destructive m-0 text-xs">
                                {e.path}: {e.message}
                            </p>
                        ))}
                        {preview.valid ? (
                            <p className="text-success m-0 text-xs">Valid.</p>
                        ) : null}
                    </div>
                ) : null}
            </div>
        </FormSheet>
    );
}

/** The override a tenant puts on an inherited rule (decision 8): status, score and parameters by key, never the condition. */
function OverrideSheet({
    rule,
    profile,
    onClose,
}: {
    rule: Row;
    profile: RiskProfile;
    onClose: () => void;
}) {
    const [status, setStatus] = useState<string>(rule.status);
    const [score, setScore] = useState(String(rule.score));
    const [params, setParams] = useState<{ k: string; v: string }[]>(
        Object.entries((rule.params as Record<string, unknown>) ?? {}).map(
            ([k, v]) => ({ k, v: String(v) }),
        ),
    );
    const [reason, setReason] = useState('');
    const [error, setError] = useState<unknown>(null);
    const write = useWrite(
        async (clear: boolean) =>
            (
                await api().put(
                    `${PROFILES}/${profile.id}/overrides/${rule.key}`,
                    clear
                        ? {}
                        : {
                              status,
                              score: Number.parseInt(score, 10) || 0,
                              reason: reason || null,
                              params: Object.fromEntries(
                                  params
                                      .filter((p) => p.k.trim())
                                      .map((p) => [
                                          p.k.trim(),
                                          paramValue(p.v),
                                      ]),
                              ),
                          },
                )
            ).data,
        [PATH, PROFILES],
    );
    return (
        <FormSheet
            open
            onOpenChange={(o) => (o ? null : onClose())}
            size="sm"
            title={`Override ${rule.key}`}
            submitLabel="Save override"
            busy={write.isPending}
            dirty
            error={error}
            data-testid="risk-override-form"
            onSubmit={(e) => {
                e.preventDefault();
                setError(null);
                write.mutate(false, { onSuccess: onClose, onError: setError });
            }}
        >
            <p className="text-fg-2 m-0 text-sm">
                {rule.name}: {rule.condition_text}
            </p>
            <Field id="ov-status" label="Status">
                <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger id="ov-status">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {STATUSES.map((s) => (
                            <SelectItem key={s} value={s}>
                                {s}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </Field>
            <Field id="ov-score" label="Score">
                <Input
                    id="ov-score"
                    inputMode="numeric"
                    value={score}
                    onChange={(e) => setScore(e.target.value)}
                />
            </Field>
            {params.map((p, i) => (
                <Field key={p.k} id={`ov-p-${i}`} label={p.k}>
                    <Input
                        id={`ov-p-${i}`}
                        value={p.v}
                        onChange={(e) =>
                            setParams(
                                params.map((q, j) =>
                                    j === i ? { ...q, v: e.target.value } : q,
                                ),
                            )
                        }
                    />
                </Field>
            ))}
            <Field id="ov-reason" label="Reason">
                <Input
                    id="ov-reason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                />
            </Field>
            {rule.overridden ? (
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                        write.mutate(true, {
                            onSuccess: onClose,
                            onError: setError,
                        })
                    }
                >
                    Clear the override
                </Button>
            ) : null}
        </FormSheet>
    );
}

/** BM-125 (E4): what the publish will change against the last published version, then the publish itself. */
export function PublishDialog({
    profile,
    onClose,
}: {
    profile: RiskProfile;
    onClose: () => void;
}) {
    const diff = useOne<RiskProfileDiff>(`${PROFILES}/${profile.id}/diff`);
    const d = diff.data?.data;
    const publish = useWrite(
        async (reason: string) =>
            (
                await api().post<{ version: number }>(
                    `${PROFILES}/${profile.id}/publish`,
                    { reason: reason || null },
                )
            ).data,
        [PATH, PROFILES],
    );
    return (
        <ConfirmDialog
            open
            onOpenChange={(o) => (o ? null : onClose())}
            title={`Publish ${profile.name}`}
            confirmLabel={`Publish version ${profile.version + 1}`}
            busy={publish.isPending}
            error={publish.error ? apiErrorMessage(publish.error) : undefined}
            reason={{ label: 'Reason', required: false }}
            onConfirm={({ reason }) =>
                publish.mutate(reason, { onSuccess: onClose })
            }
            body="What changes against the last published version."
        >
            {d ? (
                <div className="space-y-1 text-sm" data-testid="publish-diff">
                    <span className="block">
                        Added: {d.added.length ? d.added.join(', ') : 'none'}
                    </span>
                    <span className="block">
                        Removed:{' '}
                        {d.removed.length ? d.removed.join(', ') : 'none'}
                    </span>
                    <span className="block">
                        Changed:{' '}
                        {d.changed.length
                            ? d.changed.map((c) => c.key).join(', ')
                            : 'none'}
                    </span>
                    <span className="block">
                        Bands: {JSON.stringify(d.bands_to)}
                    </span>
                    {/* BM-125 slice 2 (QA): the review and graph params changed since the last publish */}
                    <span className="block" data-testid="publish-params">
                        Params: {paramsChange(d.params_from, d.params_to)}
                    </span>
                    <span className="text-fg-2 block">
                        {d.stores} store set{d.stores === 1 ? '' : 's'} rebuilt
                        at once.
                    </span>
                </div>
            ) : (
                <span className="text-fg-2 block text-sm">
                    Comparing with the published version…
                </span>
            )}
        </ConfirmDialog>
    );
}

/** The profile's own params that differ from the last publish, as "name from → to"; "no change" when none do. */
export function paramsChange(
    from: Record<string, unknown> | null | undefined,
    to: Record<string, unknown> | null | undefined,
): string {
    const a = from ?? {};
    const b = to ?? {};
    const keys = [...new Set([...Object.keys(a), ...Object.keys(b)])].sort();
    const changed = keys
        .filter((k) => a[k] !== b[k])
        .map((k) => `${k} ${a[k] ?? 'inherited'} → ${b[k] ?? 'inherited'}`);
    return changed.length ? changed.join(', ') : 'no change';
}

export function RiskRulesPage() {
    const can = useAuthStore((s) => s.can);
    const platformHost = useIsPlatformHost();
    const [editing, setEditing] = useState<Row | null | 'new'>(null);
    const [overriding, setOverriding] = useState<Row | null>(null);
    const [publishing, setPublishing] = useState(false);
    const [dependents, setDependents] = useState<{
        key: string;
        keys: string[];
    } | null>(null);
    const [stats, setStats] = useState<Row | null>(null);
    const fields = useOne<RiskRuleField[]>(`${PATH}/fields`);
    const lists = useOne<RiskList[]>('/staff/risk/lists');
    const profiles = useOne<RiskProfile[]>(PROFILES);
    // the board's own profile: the platform's on the platform host, the tenant's elsewhere
    const own = useMemo(
        () =>
            (profiles.data?.data ?? []).find((p) =>
                platformHost ? p.scope === 'platform' : p.scope === 'tenant',
            ) ?? null,
        [profiles.data, platformHost],
    );
    const actionsColumn: ListColumnDef<Row> = {
        key: 'actions',
        label: 'Actions',
        actions: true,
        cell: (r) => (
            <RowActions
                name={r.key}
                items={[
                    can(P.riskRulesManage) &&
                        (r.origin ?? r.source) === ownOrigin && {
                            label: 'Edit',
                            onSelect: () => setEditing(r),
                        },
                    can(P.riskRulesManage) &&
                        (r.origin ?? r.source) !== ownOrigin &&
                        own && {
                            label: r.overridden ? 'Edit override' : 'Override',
                            onSelect: () => setOverriding(r),
                        },
                    ...(can(P.riskRulesManage) &&
                    (r.origin ?? r.source) === ownOrigin
                        ? STATUSES.filter((s) => s !== r.status).map((s) => ({
                              key: s,
                              label:
                                  s === 'active'
                                      ? 'Activate'
                                      : s === 'shadow'
                                        ? 'Shadow'
                                        : s === 'retired'
                                          ? 'Retire'
                                          : 'Back to draft',
                              destructive: s === 'retired',
                              onSelect: () =>
                                  setStatus.mutate({
                                      id: r.id,
                                      status: s,
                                  }),
                          }))
                        : []),
                    { label: 'Stats', onSelect: () => setStats(r) },
                    {
                        label: 'Dependents',
                        onSelect: () =>
                            void api()
                                .get<{ dependents: string[] }>(
                                    `${PATH}/${r.id}`,
                                )
                                .then((res) =>
                                    setDependents({
                                        key: r.key,
                                        keys: res.dependents,
                                    }),
                                ),
                    },
                ]}
            />
        ),
    };
    const { state, query } = useAdminList<Row>(PAGE_KEY, PATH, {
        columns: [...columns, actionsColumn],
        filters: ruleFilters,
    });
    const setStatus = useWrite(
        async ({ id, status }: { id: string; status: string }) =>
            (await api().patch(`${PATH}/${id}`, { status })).data,
        [PATH, PROFILES],
    );
    const ownOrigin = platformHost ? 'platform' : 'tenant';
    // slice 5: the 30 day totals of the board's profile, joined on the rows by key
    const profileStats = useOne<RiskProfileStats>(
        own ? `${PROFILES}/${own.id}/stats` : null,
    );
    const hits = useMemo(
        () =>
            Object.fromEntries(
                (profileStats.data?.data.rules ?? []).map((r) => [
                    r.rule_key,
                    r.hits,
                ]),
            ),
        [profileStats.data],
    );
    const rows = query.rows.map((r) => ({ ...r, hits_30d: hits[r.key] }));
    const ruleKeys = rows.map((r) => r.key);
    return (
        <div>
            <PageHeader
                title="Risk rules"
                meta={
                    platformHost
                        ? 'The platform defaults every tenant inherits; a tenant overrides them in its profile.'
                        : 'The rules your checks run: the platform defaults (override them by key) and your own.'
                }
                primaryAction={
                    can(P.riskRulesManage) ? (
                        <Button onClick={() => setEditing('new')}>
                            New rule
                        </Button>
                    ) : undefined
                }
                secondaryAction={
                    can(P.riskRulesManage) && own ? (
                        <Button
                            variant="outline"
                            onClick={() => setPublishing(true)}
                        >
                            Publish
                        </Button>
                    ) : undefined
                }
            />
            <AdminListPage
                noun="rules"
                state={state}
                query={{ ...query, rows }}
                rowKey={(r) => r.id}
            />
            {stats ? (
                <RuleStatsDialog rule={stats} onClose={() => setStats(null)} />
            ) : null}
            {editing !== null ? (
                <RuleSheet
                    rule={editing === 'new' ? null : editing}
                    profile={own}
                    fields={fields.data?.data ?? []}
                    lists={lists.data?.data ?? []}
                    ruleKeys={ruleKeys}
                    onClose={() => setEditing(null)}
                />
            ) : null}
            {overriding && own ? (
                <OverrideSheet
                    rule={overriding}
                    profile={own}
                    onClose={() => setOverriding(null)}
                />
            ) : null}
            {publishing && own ? (
                <PublishDialog
                    profile={own}
                    onClose={() => setPublishing(false)}
                />
            ) : null}
            {dependents ? (
                <ConfirmDialog
                    open
                    onOpenChange={(o) => (o ? null : setDependents(null))}
                    title={`Rules that use ${dependents.key}`}
                    confirmLabel="Close"
                    onConfirm={() => setDependents(null)}
                    // BM-158 self-review: the body is the dialog's <p> description, so it takes text, never a <p>
                    body={
                        dependents.keys.length
                            ? dependents.keys.join(', ')
                            : 'No rule composes this one.'
                    }
                />
            ) : null}
        </div>
    );
}
